<script setup lang="ts">
/**
 * SignersField — package PresetMVP 문서 서명 UI (렌더 그리드 + 편집 칩).
 * Add Signers 모달만 기존 SignerListField (실 entity officers) 를 사용.
 */
import { ref, computed, nextTick } from 'vue';
import SignerListField from '@/features/AI-documents/components/SignerListField.vue';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';

const props = defineProps<{
  modelValue: SignerEntry[];
  boardLabel?: string;
  entityCd?: string;
  varKey?: string;
  /** 문서가 1인 가운데 정렬을 고정으로 쓰는 경우(예: Singapura AGM 1.a/1.c/1.e) — 옵션을 체크 상태로 잠근다. */
  centerLocked?: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [SignerEntry[]];
  update: [string];
}>();

const signers = computed(() => props.modelValue || []);
const signerRows = computed(() => {
  const out: SignerEntry[][] = [];
  for (let i = 0; i < signers.value.length; i += 2) {
    out.push(signers.value.slice(i, i + 2));
  }
  return out;
});
/** L_ENTITY 없으면 officer 선택 다이얼로그 불가 — 수동 입력만 */
const canPickOfficers = computed(() => !!String(props.entityCd || '').trim());

const editing = ref(false);
const manualName = ref('');
const manualInputRef = ref<HTMLInputElement | null>(null);
const listRef = ref<InstanceType<typeof SignerListField> | null>(null);

/**
 * 서명자 1명일 때 가운데 정렬 — 체크 상태는 그 서명자 항목의 _signCenter 로 저장한다.
 *   서명자 목록이 그대로 문서 생성(백엔드 replaceSignerBlocks)까지 가므로 패널마다 따로 넘길 필요가 없다.
 */
const isSingle = computed(() => signers.value.length === 1);
const centered = computed(() => isSingle.value && (!!props.centerLocked || !!signers.value[0]?._signCenter));
function toggleCenter(on: boolean) {
  if (!isSingle.value || props.centerLocked) return;
  set([{ ...signers.value[0], _signCenter: on }]);
}
/** 1명 → 1명으로 바뀌어도(서명자 교체) 가운데 정렬 선택은 유지 */
function keepCenter(list: SignerEntry[]): SignerEntry[] {
  const prev = signers.value;
  if (list.length === 1 && prev.length === 1 && prev[0]?._signCenter && list[0]._signCenter === undefined) {
    return [{ ...list[0], _signCenter: true }];
  }
  return list;
}

function set(input: SignerEntry[]) {
  const list = keepCenter(input);
  emit('update:modelValue', list);
  emit('update', JSON.stringify(list));
}

function onListUpdate(raw: string) {
  try {
    const parsed = JSON.parse(raw);
    const list = keepCenter(Array.isArray(parsed) ? (parsed as SignerEntry[]) : []);
    emit('update:modelValue', list);
    emit('update', JSON.stringify(list));
    if (list.length) editing.value = false;
  } catch {
    /* ignore */
  }
}

function openAddModal() {
  editing.value = true;
  if (canPickOfficers.value) {
    listRef.value?.openDialog?.();
    return;
  }
  // officer 목록 없음 — 수동 입력란에 포커스 (가짜 Add Signers 동작 방지)
  nextTick(() => manualInputRef.value?.focus());
}

function openManualEdit() {
  editing.value = true;
  nextTick(() => manualInputRef.value?.focus());
}

function removeChip(id: string) {
  set(signers.value.filter((s) => s.id !== id));
}
function clearAll() {
  set([]);
}
function addManual() {
  const name = manualName.value.trim();
  if (!name) return;
  set([
    ...signers.value,
    {
      id: `manual-${Date.now()}-${name}`,
      name,
      title: '',
      role: 'manual',
      isManual: true,
      showTitle: false,
      _signerLayout: 'box',
    },
  ]);
  manualName.value = '';
}

function titleOf(s: SignerEntry): string {
  if (s.showTitle === false) return '';
  return String(s.customTitle || s.title || s.role || '').trim();
}
</script>

<template>
  <div class="sgf">
    <template v-if="!editing">
      <div v-if="boardLabel" class="sgf-board">{{ boardLabel }}</div>

      <button
        v-if="!signers.length"
        type="button"
        class="sgf-empty"
        @click="canPickOfficers ? openAddModal() : openManualEdit()"
      >
        <v-icon size="16">mdi-account-plus-outline</v-icon>
        {{ canPickOfficers ? 'Add signers' : 'Add signers manually' }}
      </button>

      <template v-else>
        <div class="sgf-rows">
          <div v-for="(row, ri) in signerRows" :key="ri" class="sgf-row" :class="{ 'sgf-row--center': centered }">
            <div v-for="s in row" :key="s.id" class="sgf-cell">
              <div class="sgf-line">______________________________</div>
              <div class="sgf-name">{{ s.name }}</div>
              <div v-if="titleOf(s)" class="sgf-title">{{ titleOf(s) }}</div>
            </div>
          </div>
        </div>
        <div class="sgf-actions">
          <button type="button" class="sgf-edit" @click="editing = true">
            <v-icon size="14">mdi-pencil-outline</v-icon> Edit signers
          </button>
          <label
            v-if="isSingle"
            class="sgf-center"
            :class="{ 'is-locked': centerLocked }"
            :title="centerLocked ? 'This document always centers a single signature.' : 'Center the signature when there is only one signer.'"
          >
            <input
              type="checkbox"
              :checked="centered"
              :disabled="centerLocked"
              @change="toggleCenter(($event.target as HTMLInputElement).checked)"
            />
            Center signature
          </label>
        </div>
      </template>
    </template>

    <div v-else class="sgf-editor">
      <div class="sgf-ed-head">
        <button type="button" class="sgf-ed-close" title="Done" @click="editing = false">
          <v-icon size="16">mdi-close</v-icon>
        </button>
        <span class="sgf-ed-t">Signers <b>*</b></span>
        <button type="button" class="sgf-clear" :disabled="!signers.length" @click="clearAll">
          <v-icon size="14">mdi-trash-can-outline</v-icon> Clear All
        </button>
        <button
          v-if="canPickOfficers"
          type="button"
          class="sgf-addbtn"
          @click="openAddModal"
        >
          <v-icon size="14">mdi-account-multiple-plus-outline</v-icon> Add Signers
        </button>
      </div>

      <p v-if="!canPickOfficers" class="sgf-hint">
        Officers list is unavailable until the company is incorporated. Add or remove names below.
      </p>

      <div v-if="signers.length" class="sgf-chips">
        <span v-for="s in signers" :key="s.id" class="sgf-chip">
          <v-icon size="13">mdi-account</v-icon>{{ s.name }}
          <button type="button" class="sgf-chip-x" @click="removeChip(s.id)">
            <v-icon size="13">mdi-close-circle</v-icon>
          </button>
        </span>
      </div>

      <div class="sgf-manual">
        <input
          ref="manualInputRef"
          v-model="manualName"
          placeholder="Enter name manually…"
          @keydown.enter="addManual"
        />
        <button type="button" class="sgf-manual-add" :disabled="!manualName.trim()" @click="addManual">
          <v-icon size="18">mdi-plus</v-icon>
        </button>
      </div>
      <button type="button" class="sgf-done" @click="editing = false">Done</button>
    </div>

    <!-- 모달 호스트: 기존 SignerListField UI는 숨기고 Select Signers 다이얼로그만 사용 -->
    <div class="sgf-modal-host" aria-hidden="true">
        <SignerListField
          ref="listRef"
          :model-value="signers"
          :label="boardLabel || 'Signers'"
          :var-key="varKey || 'signers'"
          :entity-cd-override="entityCd || ''"
          :manual-only="!String(entityCd || '').trim()"
          @update="onListUpdate"
        />
    </div>
  </div>
</template>

<style scoped>
.sgf { margin:22px 0 6px; }
.sgf-board { font-weight:800; font-size:12.5px; text-align:center; margin-bottom:18px; }

.sgf-rows { display:block; }
.sgf-row { display:grid; grid-template-columns:1fr 1fr; gap:40px; margin-top:30px; }
.sgf-row:first-child { margin-top:6px; }
.sgf-cell { min-width:0; text-align:center; }
/* 홀수 마지막 사이너 — 2열 그리드이므로 자동으로 첫 칸(1fr)만 차지한다.
   ⚠ 예전엔 여기에 max-width:calc((100% - 40px)/2) + justify-self:start 를 걸었는데,
   % 기준이 '행'이 아니라 이미 반폭인 '컬럼'이라 폭이 한 번 더 반토막(312→136px)나서
   밑줄이 짧아지고 긴 이름이 두 줄로 깨졌다(위 칸들과 중심선 불일치). 규칙 제거가 정답. */
/* 서명자 1명 + 가운데 정렬 — 칸 폭은 2열일 때 한 칸과 같게(밑줄 길이 유지) 두고 가운데로. */
.sgf-row--center { grid-template-columns:minmax(0, calc((100% - 40px) / 2)); justify-content:center; }
.sgf-line { letter-spacing:1px; color:#333; white-space:nowrap; }
.sgf-name { font-size:12.5px; font-weight:700; margin-top:3px; white-space:nowrap; }
.sgf-title { font-size:11.5px; color:#42506a; margin-top:1px; }
.sgf-edit { display:inline-flex; align-items:center; gap:4px; margin-top:16px; border:1px solid #d3e2ff; background:#f4f8ff; color:#1B64DA; font-size:11.5px; font-weight:800; border-radius:8px; padding:5px 11px; cursor:pointer; }
.sgf-edit:hover { background:#e6f0ff; }
.sgf-actions { display:flex; align-items:center; flex-wrap:wrap; gap:12px; }
.sgf-actions .sgf-edit { margin-top:16px; }
.sgf-center { display:inline-flex; align-items:center; gap:5px; margin-top:16px; font-size:11.5px; font-weight:700; color:#42506a; cursor:pointer; user-select:none; }
.sgf-center input { accent-color:#3182F6; cursor:pointer; }
.sgf-center.is-locked { color:#8B95A1; cursor:default; }
.sgf-center.is-locked input { cursor:default; }
.sgf-empty { display:inline-flex; align-items:center; gap:6px; border:1.5px dashed #b9c9e6; background:#f7faff; color:#1B64DA; font-size:12.5px; font-weight:800; border-radius:10px; padding:11px 18px; cursor:pointer; }
.sgf-empty:hover { background:#eef4ff; border-color:#8fb2ee; }

.sgf-editor { border:1px solid #e2e8f2; border-radius:14px; padding:14px; background:#fbfcfe; font-family:'Inter','Apple SD Gothic Neo',-apple-system,sans-serif; }
.sgf-ed-head { display:flex; align-items:center; gap:9px; }
.sgf-ed-close { border:none; background:none; color:#8B95A1; cursor:pointer; display:inline-flex; padding:0; }
.sgf-ed-t { font-size:13.5px; font-weight:800; color:#191F28; } .sgf-ed-t b { color:#e5484d; }
.sgf-hint { margin:10px 0 0; font-size:12px; color:#6b7a94; line-height:1.45; }
.sgf-clear { margin-left:auto; display:inline-flex; align-items:center; gap:4px; border:none; background:#fdecec; color:#d64545; font-size:12px; font-weight:800; border-radius:9px; padding:7px 12px; cursor:pointer; }
.sgf-clear:disabled { opacity:.5; cursor:default; }
.sgf-addbtn { display:inline-flex; align-items:center; gap:5px; border:1.5px solid #cfe0ff; background:#fff; color:#3182F6; font-size:12.5px; font-weight:800; border-radius:9px; padding:7px 13px; cursor:pointer; }
.sgf-addbtn:hover { background:#f0f6ff; }
.sgf-chips { display:flex; flex-wrap:wrap; gap:8px; margin-top:13px; }
.sgf-chip { display:inline-flex; align-items:center; gap:5px; border:1.5px solid #bcd4ff; background:#fff; color:#1f66d6; font-size:12.5px; font-weight:700; border-radius:20px; padding:6px 8px 6px 11px; }
.sgf-chip :deep(.v-icon) { color:#3182F6; }
.sgf-chip-x { border:none; background:none; cursor:pointer; display:inline-flex; padding:0; } .sgf-chip-x :deep(.v-icon) { color:#3182F6; }
.sgf-manual { display:flex; align-items:center; gap:8px; margin-top:13px; }
.sgf-manual input { flex:1; border:1.5px solid #e2e8f2; border-radius:11px; padding:11px 13px; font-size:13.5px; outline:none; background:#fff; }
.sgf-manual input:focus { border-color:#3182F6; }
.sgf-manual-add { width:40px; height:40px; border-radius:11px; border:1.5px solid #e2e8f2; background:#f2f4f6; color:#8B95A1; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; flex:0 0 auto; }
.sgf-manual-add:not(:disabled):hover { background:#3182F6; color:#fff; border-color:#3182F6; }
.sgf-manual-add:disabled { cursor:default; }
.sgf-done { margin-top:12px; border:none; background:#3182F6; color:#fff; font-size:13px; font-weight:800; border-radius:10px; padding:9px 18px; cursor:pointer; }

.sgf-modal-host {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}
</style>
